import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import api from '../lib/api';
import { getApiErrorMessage } from '../lib/apiErrors';
import { planCapabilityDisplay } from '../lib/planCapabilities';
import { CrmIntegrationSection } from '../components/crm/CrmIntegrationSection';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { isOrgAdmin } from '../lib/orgRoles';
import {
  RECORDING_VISIBILITY_OPTIONS,
  SCORES_ALWAYS_VISIBLE_NOTE,
} from '../lib/recordingVisibility';
import type { RecordingVisibility } from '../components/practice/types';

function isValidEmail(value: string): boolean {
  const t = value.trim();
  if (!t) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t);
}

interface OrgMember {
  id: string;
  email: string;
  name: string | null;
  role: string;
}

interface PendingInvitation {
  id: string;
  email: string;
  role: string;
  inviteUrl: string;
  expiresAt: string;
  createdAt: string;
}

interface OrgInfo {
  id: string;
  name: string;
  domain: string;
  members: OrgMember[];
  plan?: string;
  subscriptionStatus?: string;
  trialEndsAt?: string | null;
  features?: string[];
  planLabel?: string;
  trialExpired?: boolean;
  defaultRecordingVisibility?: RecordingVisibility;
}

function RecordingPrivacySection({
  value,
  onSaved,
}: {
  value: RecordingVisibility;
  onSaved: (value: RecordingVisibility) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = async (next: RecordingVisibility) => {
    setSaving(true);
    setError(null);
    try {
      await api.patch('/orgs/me', { defaultRecordingVisibility: next });
      onSaved(next);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white p-6 rounded-lg border mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Session review privacy</h2>
      <p className="text-sm text-gray-600 mb-4">
        Default for who can read a rep&apos;s practice transcript and written feedback. Reps can
        change it for each session. {SCORES_ALWAYS_VISIBLE_NOTE}
      </p>
      <label
        htmlFor="settings-recording-visibility"
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        Default for new sessions
      </label>
      <select
        id="settings-recording-visibility"
        value={value}
        disabled={saving}
        onChange={(e) => void handleChange(e.target.value as RecordingVisibility)}
        className="w-full sm:w-72 border rounded-md px-3 py-2 text-sm bg-white border-gray-300 disabled:opacity-50"
      >
        {RECORDING_VISIBILITY_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <p className="text-xs text-gray-500 mt-2">
        {RECORDING_VISIBILITY_OPTIONS.find((o) => o.value === value)?.help}
      </p>
      {error && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

function RoleSelect({
  member,
  onRoleChange,
}: {
  member: OrgMember;
  onRoleChange: (userId: string, role: string) => Promise<void>;
}) {
  const [updating, setUpdating] = useState(false);

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    setUpdating(true);
    try {
      await onRoleChange(member.id, e.target.value);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <select
      value={member.role}
      onChange={handleChange}
      disabled={updating}
      aria-label={`Role for ${member.name || member.email}`}
      className="text-xs px-2 py-1 bg-white border border-gray-300 rounded font-medium focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent disabled:opacity-50"
    >
      <option value="admin">admin</option>
      <option value="manager">manager</option>
      <option value="user">user</option>
    </select>
  );
}

function canRemoveMember(
  member: OrgMember,
  currentUserId: string | undefined,
  orgRole: string | undefined,
): boolean {
  if (!currentUserId || !orgRole) return false;
  if (member.id === currentUserId) return false;
  if (member.role === 'owner') return false;
  if (orgRole === 'admin') return member.role === 'user' || member.role === 'manager';
  if (orgRole === 'owner')
    return member.role === 'admin' || member.role === 'manager' || member.role === 'user';
  return false;
}

export function SettingsPage() {
  const { user } = useAuthContext();
  const [org, setOrg] = useState<OrgInfo | null>(null);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'user' | 'admin' | 'manager'>('user');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [rescindingId, setRescindingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [copiedInviteId, setCopiedInviteId] = useState<string | null>(null);
  const [invitationIdToRemove, setInvitationIdToRemove] = useState<string | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<{
    id: string;
    displayName: string;
  } | null>(null);

  const loadOrg = useCallback(async () => {
    const res = await api.get('/orgs/me');
    setOrg(res.data);
  }, []);

  const loadInvitations = useCallback(async () => {
    const res = await api.get('/orgs/me/invitations');
    const data = res.data;
    setPendingInvitations(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => {
    loadOrg().catch(() => {});
  }, [loadOrg]);

  const canManageMembers = isOrgAdmin(user?.orgRole);

  useEffect(() => {
    if (!canManageMembers) return;
    loadInvitations().catch(() => {});
  }, [canManageMembers, loadInvitations]);

  useEffect(() => {
    if (user?.orgRole === 'admin') setInviteRole('user');
  }, [user?.orgRole]);

  const isOwner = user?.orgRole === 'owner';

  const handleRoleChange = async (userId: string, role: string) => {
    await api.patch(`/orgs/me/members/${userId}/role`, { role });
    setOrg((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        members: prev.members.map((m) => (m.id === userId ? { ...m, role } : m)),
      };
    });
  };

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inviteEmail.trim();
    if (!isValidEmail(trimmed)) return;
    setInviting(true);
    setInviteError(null);
    try {
      await api.post('/orgs/me/invites', { email: trimmed, role: inviteRole });
      setInviteEmail('');
      await loadInvitations();
    } catch (err) {
      setInviteError(getApiErrorMessage(err));
    } finally {
      setInviting(false);
    }
  };

  const confirmRescindInvite = async () => {
    if (!invitationIdToRemove) return;
    const id = invitationIdToRemove;
    setInvitationIdToRemove(null);
    setRescindingId(id);
    try {
      await api.delete(`/orgs/me/invitations/${id}`);
      await loadInvitations();
    } catch {
      // best-effort
    } finally {
      setRescindingId(null);
    }
  };

  const handleResendInvite = async (id: string) => {
    setResendingId(id);
    try {
      await api.post(`/orgs/me/invitations/${id}/resend`);
      await loadInvitations();
    } catch (err) {
      window.alert(getApiErrorMessage(err));
    } finally {
      setResendingId(null);
    }
  };

  const confirmRemoveMember = async () => {
    if (!memberToRemove) return;
    const { id: userId } = memberToRemove;
    setMemberToRemove(null);
    setRemovingUserId(userId);
    try {
      await api.delete(`/orgs/me/members/${userId}`);
      await loadOrg();
    } catch (err) {
      window.alert(getApiErrorMessage(err));
    } finally {
      setRemovingUserId(null);
    }
  };

  if (!org) return <div className="text-gray-500">Loading...</div>;

  const trialEnds = org.trialEndsAt ? new Date(org.trialEndsAt) : null;
  const trialDaysLeft =
    trialEnds && !org.trialExpired && org.subscriptionStatus === 'trialing'
      ? Math.max(0, Math.ceil((trialEnds.getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
      : null;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-8">Organization Settings</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 items-start">
        {org.planLabel && (
          <section className="bg-white p-6 rounded-lg border h-full">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Plan</h2>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-500">Current:</span>{' '}
                <span className="font-medium">{org.planLabel}</span>
              </div>
              {trialDaysLeft !== null && (
                <div className="text-gray-600">
                  {trialDaysLeft === 0
                    ? 'Trial ends today.'
                    : `${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left in trial.`}
                </div>
              )}
              {org.trialExpired && (
                <div className="text-gray-600">
                  Your trial has ended. Upgrade to restore full access.
                </div>
              )}
              {org.features && org.features.length > 0 && (
                <div className="pt-3 border-t border-gray-100 mt-3">
                  <p className="text-gray-500 text-sm font-medium">Included capabilities</p>
                  <ul className="mt-3 space-y-3">
                    {org.features.map((f) => {
                      const { label, description } = planCapabilityDisplay(f);
                      return (
                        <li
                          key={f}
                          className="rounded-md border border-gray-100 bg-gray-50/80 px-3 py-2.5"
                        >
                          <div className="font-medium text-gray-900">{label}</div>
                          <p className="text-gray-600 text-sm mt-1 leading-snug">{description}</p>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {org.features && org.features.length === 0 && (
                <p className="text-gray-600">No premium add-on capabilities on this plan.</p>
              )}
            </div>
          </section>
        )}

        <section
          className={`bg-white p-6 rounded-lg border ${org.planLabel ? '' : 'lg:col-span-2'}`}
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Organization</h2>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-500">Name:</span>{' '}
              <span className="font-medium">{org.name}</span>
            </div>
            <div>
              <span className="text-gray-500">Domain:</span>{' '}
              <span className="font-medium">{org.domain}</span>
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-3">
            <Link to="/onboarding" className="text-green-700 hover:text-green-800 font-medium">
              Update business profile
            </Link>
            <span className="text-gray-500"> — then use </span>
            <Link to="/personas" className="text-green-700 hover:text-green-800 font-medium">
              Personas
            </Link>
            <span className="text-gray-500"> to regenerate AI personas from your answers.</span>
          </p>
        </section>
      </div>

      <section className="bg-white p-6 rounded-lg border mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Members</h2>
        {canManageMembers && org.members.length === 0 && pendingInvitations.length === 0 ? (
          <p className="text-sm text-gray-600 mb-4">No members yet.</p>
        ) : null}
        <div className="divide-y">
          {org.members.map((m) => (
            <div key={m.id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="font-medium text-gray-900 truncate">{m.name || m.email}</div>
                <div className="text-sm text-gray-500 truncate">{m.email}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {isOwner && m.role !== 'owner' ? (
                  <RoleSelect member={m} onRoleChange={handleRoleChange} />
                ) : (
                  <span className="text-xs px-2 py-1 bg-gray-100 rounded font-medium">
                    {m.role}
                  </span>
                )}
                {canRemoveMember(m, user?.id, user?.orgRole ?? undefined) && (
                  <button
                    type="button"
                    onClick={() => setMemberToRemove({ id: m.id, displayName: m.name || m.email })}
                    disabled={removingUserId === m.id}
                    className="text-xs px-2 py-1 text-red-700 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50"
                  >
                    {removingUserId === m.id ? 'Removing…' : 'Remove'}
                  </button>
                )}
              </div>
            </div>
          ))}
          {canManageMembers &&
            pendingInvitations.map((inv) => (
              <div
                key={inv.id}
                className="py-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-900 truncate">{inv.email}</span>
                    <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-800 rounded font-medium shrink-0">
                      Pending
                    </span>
                    <span className="text-xs text-gray-500">{inv.role}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Sent {new Date(inv.createdAt).toLocaleString()} · Expires{' '}
                    {new Date(inv.expiresAt).toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      void navigator.clipboard.writeText(inv.inviteUrl);
                      setCopiedInviteId(inv.id);
                      setTimeout(() => setCopiedInviteId(null), 2000);
                    }}
                    aria-label={`Copy invite link for ${inv.email}`}
                    className="text-xs px-2 py-1 text-blue-800 border border-blue-200 rounded hover:bg-blue-50"
                  >
                    {copiedInviteId === inv.id ? 'Copied!' : 'Copy link'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleResendInvite(inv.id)}
                    disabled={resendingId === inv.id || rescindingId === inv.id}
                    aria-label={`Resend invitation to ${inv.email}`}
                    className="text-xs px-2 py-1 text-green-800 border border-green-200 rounded hover:bg-green-50 disabled:opacity-50"
                  >
                    {resendingId === inv.id ? 'Sending…' : 'Resend'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setInvitationIdToRemove(inv.id)}
                    disabled={rescindingId === inv.id || resendingId === inv.id}
                    className="text-xs px-2 py-1 text-red-700 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50"
                  >
                    {rescindingId === inv.id ? 'Removing…' : 'Remove'}
                  </button>
                </div>
              </div>
            ))}
        </div>
      </section>

      {canManageMembers && (
        <section className="bg-white p-6 rounded-lg border mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Invite people</h2>
          <p className="text-sm text-gray-600 mb-4">
            Sends a sign-in link by email.{' '}
            {isOwner ? 'You can invite users or admins.' : 'You can invite users.'}
          </p>
          <form
            onSubmit={handleSendInvite}
            className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <div className="flex-1 min-w-[200px]">
              <label
                htmlFor="settings-invite-email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email
              </label>
              <input
                id="settings-invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm border-gray-300"
                placeholder="colleague@company.com"
                disabled={inviting}
                autoComplete="email"
              />
            </div>
            <div className="w-full sm:w-40">
              <label
                htmlFor="settings-invite-role"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Role when they join
              </label>
              <select
                id="settings-invite-role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as 'user' | 'admin' | 'manager')}
                className="w-full border rounded-md px-3 py-2 text-sm bg-white border-gray-300"
                disabled={inviting}
              >
                <option value="user">User</option>
                <option value="manager">Manager</option>
                {isOwner && <option value="admin">Admin</option>}
              </select>
            </div>
            <button
              type="submit"
              disabled={inviting || !isValidEmail(inviteEmail)}
              className="px-4 py-2 bg-green-700 text-white text-sm font-medium rounded-md hover:bg-green-800 disabled:opacity-50"
            >
              {inviting ? 'Sending…' : 'Send invite'}
            </button>
          </form>
          {inviteError && <p className="mt-3 text-sm text-red-600">{inviteError}</p>}
        </section>
      )}

      {canManageMembers && (
        <RecordingPrivacySection
          value={org.defaultRecordingVisibility ?? 'rep_only'}
          onSaved={(next) =>
            setOrg((prev) => (prev ? { ...prev, defaultRecordingVisibility: next } : prev))
          }
        />
      )}

      {canManageMembers && org.features?.includes('crm_connect') && <CrmIntegrationSection />}

      <ConfirmDialog
        open={invitationIdToRemove !== null}
        title="Remove invitation?"
        description="Remove this invitation? The invite link will no longer work."
        confirmLabel="Remove"
        variant="destructive"
        onConfirm={() => void confirmRescindInvite()}
        onCancel={() => setInvitationIdToRemove(null)}
        loading={rescindingId !== null}
      />
      <ConfirmDialog
        open={memberToRemove !== null}
        title="Remove member?"
        description={
          memberToRemove ? `Remove ${memberToRemove.displayName} from this workspace?` : ''
        }
        confirmLabel="Remove"
        variant="destructive"
        onConfirm={() => void confirmRemoveMember()}
        onCancel={() => setMemberToRemove(null)}
        loading={removingUserId !== null}
      />
    </div>
  );
}
